import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Grid, LinearProgress } from '@mui/material';

import { HeroPlannerDebugSummary as Summary } from '../../utils/heroPlannerDebugUtils';

import HeroPlannerDebugStatCard from './HeroPlannerDebugStatCard';

function percentage(part: number, total: number) {
  return total === 0 ? 0 : Math.round((part / total) * 100);
}

interface Props {
  summary: Summary;
  offerId: string;
}

function HeroPlannerDebugSummary({ summary, offerId }: Props) {
  const failing = summary.failed + summary.regenerating;
  const checked = summary.ok + summary.heroOnly + failing;
  const isRunning = summary.loading > 0;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <HeroPlannerDebugStatCard
          label="Segments checked"
          value={`${checked} / ${summary.total}`}
          caption={[
            isRunning ? `${summary.loading} still running…` : 'Sweep complete',
            summary.skipped ? `${summary.skipped} never-sent skipped` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
          icon={<TravelExploreIcon fontSize="large" />}
          colour="primary"
          footer={
            <LinearProgress
              variant="determinate"
              value={percentage(checked, summary.total)}
              aria-label="Sweep progress"
            />
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <HeroPlannerDebugStatCard
          label="Ready to send"
          value={summary.ok}
          caption={
            summary.heroOnly
              ? `+${summary.heroOnly} waiting for tail deals (hero planner row only)`
              : `${percentage(summary.ok, checked)}% of checked segments`
          }
          icon={<CheckCircleOutlineIcon fontSize="large" />}
          colour="success"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <HeroPlannerDebugStatCard
          label="Failing"
          value={failing}
          caption={
            failing
              ? [
                  summary.failed ? `${summary.failed} errored` : '',
                  summary.regenerating ? `${summary.regenerating} regenerating` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Nothing failing'
          }
          icon={<ErrorOutlineIcon fontSize="large" />}
          colour="error"
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <HeroPlannerDebugStatCard
          label="Offer found in"
          value={offerId ? summary.withOffer : '—'}
          caption={offerId ? `segments containing ${offerId}` : 'Enter an offer ID to trace it'}
          icon={<ManageSearchIcon fontSize="large" />}
          colour="info"
        />
      </Grid>
    </Grid>
  );
}

export default HeroPlannerDebugSummary;
